import { useEffect, useRef, useState } from 'react';
import {
      collection,
      addDoc,
      getDocs,
      query,
      where,
      serverTimestamp,
      doc,
      updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../config/AuthContext';
import PageShapes from '../component/PageShapes';
import Loading from '../component/Loading';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import '../styles/movies.scss';

// ─── TMDB ─────────────────────────────────────────────────────
export const API_KEY = "92f623b495f4b244206b7ea01dd66132";
const IMG_BASE = 'https://image.tmdb.org/t/p/w500';

const TMDB_GENRE_MAP = {
      28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy',
      80: 'Crime', 99: 'Documentary', 18: 'Drama', 10751: 'Family',
      14: 'Fantasy', 36: 'History', 27: 'Horror', 10402: 'Music',
      9648: 'Mystery', 10749: 'Romance', 878: 'Sci-Fi', 10770: 'TV Movie',
      53: 'Thriller', 10752: 'War', 37: 'Western',
      // TV genres
      10759: 'Action', 10762: 'Animation', 10763: 'Drama', 10764: 'Drama',
      10765: 'Sci-Fi', 10766: 'Drama', 10767: 'Drama', 10768: 'Drama',
};

const TMDB_LANG_MAP = {
      en: 'English', hi: 'Hindi', ko: 'Korean', es: 'Spanish',
      fr: 'French', ja: 'Japanese', de: 'German', it: 'Italian',
      pt: 'Spanish', zh: 'Other', ta: 'Other', te: 'Other',
};

// ─── Constants ───────────────────────────────────────────────
const GENRES = [
      'Action', 'Comedy', 'Drama', 'Horror', 'Sci-Fi',
      'Thriller', 'Romance', 'Animation', 'Documentary', 'Fantasy',
];

const LANGUAGES = ['English', 'Hindi', 'Korean', 'Spanish', 'French', 'Japanese', 'Other'];

const PLATFORMS = ['Netflix', 'Prime Video', 'Disney+', 'Apple TV+', 'Hulu', 'HBO Max', 'YouTube', 'Theater', 'Other'];

const TABS = ['watched', 'watchlist'];

const EMPTY_FORM = {
      title: '',
      type: 'Movie',
      genre: '',
      language: 'English',
      platform: '',
      year: '',
      rating: '',
      notes: '',
      poster: '',
      status: 'watched',
};

// ─── Component ───────────────────────────────────────────────
const Movies = () => {
      const { user } = useAuth();

      const [activeTab, setActiveTab] = useState('watched');
      const [items, setItems] = useState([]);
      const [loading, setLoading] = useState(true);
      const [showModal, setShowModal] = useState(false);
      const [form, setForm] = useState(EMPTY_FORM);
      const [saving, setSaving] = useState(false);
      const [filterGenre, setFilterGenre] = useState('All');
      const [filterType, setFilterType] = useState('All');
      const [searchQ, setSearchQ] = useState('');

      // ─── Hero Search ──────────────────────────────────────────
      const [heroQuery, setHeroQuery] = useState('');
      const [heroResults, setHeroResults] = useState([]);
      const [heroSearching, setHeroSearching] = useState(false);
      const [savingId, setSavingId] = useState(null); // tmdb id being saved
      const heroRef = useRef(null);
      const debounceRef = useRef(null);

      // ─── Hero Search ──────────────────────────────────────────
      useEffect(() => {
            if (!heroQuery.trim()) { setHeroResults([]); return; }
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(async () => {
                  setHeroSearching(true);
                  try {
                        const res = await fetch(
                              `https://api.themoviedb.org/3/search/multi?api_key=${API_KEY}&language=en-US&query=${encodeURIComponent(heroQuery)}&include_adult=false`
                        );
                        const data = await res.json();
                        const filtered = (data.results || []).filter(
                              (r) => r.media_type === 'movie' || r.media_type === 'tv'
                        ).slice(0, 8);
                        setHeroResults(filtered);
                  } catch (err) {
                        console.error('TMDB search error:', err);
                  } finally {
                        setHeroSearching(false);
                  }
            }, 400);
            return () => clearTimeout(debounceRef.current);
      }, [heroQuery]);

      // close dropdown on outside click
      useEffect(() => {
            const handler = (e) => {
                  if (heroRef.current && !heroRef.current.contains(e.target)) {
                        setHeroResults([]);
                  }
            };
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
      }, []);

      // ─── Quick Save Modal ─────────────────────────────────────
      const [quickModal, setQuickModal] = useState(null); // { result, status }
      const [quickPlatform, setQuickPlatform] = useState('');
      const [quickRating, setQuickRating] = useState('');

      // ─── Already In List Modal ────────────────────────────────
      const [alreadyModal, setAlreadyModal] = useState(null); // existing item

      const openQuickModal = (result, status) => {
            // tmdbId checkpoint
            const existing = items.find((i) => i.tmdbId === result.id);
            if (existing) {
                  setAlreadyModal(existing);
                  setHeroResults([]);
                  setHeroQuery('');
                  return;
            }
            setQuickPlatform('');
            setQuickRating('');
            setQuickModal({ result, status });
            setHeroResults([]);
            setHeroQuery('');
      };

      const handleHeroSave = async () => {
            if (!user || !quickModal) return;
            const { result, status } = quickModal;
            const id = result.id;
            setSavingId(`${id}-${status}`);
            const title = result.title || result.name || 'Unknown';
            const type = result.media_type === 'tv' ? 'Series' : 'Movie';
            const year = (result.release_date || result.first_air_date || '').slice(0, 4);
            const poster = result.poster_path ? `${IMG_BASE}${result.poster_path}` : '';
            const genre = TMDB_GENRE_MAP[result.genre_ids?.[0]] || '';
            const language = TMDB_LANG_MAP[result.original_language] || result.original_language || '';
            const overview = result.overview || '';
            try {
                  const docRef = await addDoc(collection(db, 'movies'), {
                        title, type, year, poster, genre, language,
                        platform: quickPlatform,
                        rating: quickRating,
                        notes: overview,
                        status, tmdbId: id,
                        userId: user.uid,
                        userName: user.displayName || 'User',
                        createdAt: serverTimestamp(),
                  });
                  setItems((prev) => [
                        { id: docRef.id, title, type, year, poster, genre, language, platform: quickPlatform, rating: quickRating, notes: overview, status, tmdbId: id, createdAt: new Date() },
                        ...prev,
                  ]);
                  setQuickModal(null);
            } catch (err) {
                  console.error('Save from search error:', err);
            } finally {
                  setSavingId(null);
            }
      };

      // ─── Fetch ────────────────────────────────────────────────
      useEffect(() => {
            if (!user) return;
            const fetchItems = async () => {
                  setLoading(true);
                  try {
                        const q = query(
                              collection(db, 'movies'),
                              where('userId', '==', user.uid)
                        );
                        const snap = await getDocs(q);
                        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
                  } catch (err) {
                        console.error('Fetch movies error:', err);
                  } finally {
                        setLoading(false);
                  }
            };
            fetchItems();
      }, [user]);

      // ─── Save ─────────────────────────────────────────────────
      const handleSave = async () => {
            if (!form.title.trim()) return;
            setSaving(true);
            try {
                  const docRef = await addDoc(collection(db, 'movies'), {
                        ...form,
                        userId: user.uid,
                        userName: user.displayName || 'User',
                        createdAt: serverTimestamp(),
                  });
                  setItems((prev) => [{ id: docRef.id, ...form, createdAt: new Date() }, ...prev]);
                  setShowModal(false);
                  setForm(EMPTY_FORM);
            } catch (err) {
                  console.error('Save movie error:', err);
            } finally {
                  setSaving(false);
            }
      };

      const openModal = (status = 'watched') => {
            setForm({ ...EMPTY_FORM, status });
            setShowModal(true);
      };

      // ─── Mark as Watched ──────────────────────────────────────
      const handleMarkWatched = async (id) => {
            try {
                  await updateDoc(doc(db, 'movies', id), { status: 'watched' });
                  setItems((prev) =>
                        prev.map((item) => item.id === id ? { ...item, status: 'watched' } : item)
                  );
            } catch (err) {
                  console.error('Mark watched error:', err);
            }
      };

      // ─── Filter ───────────────────────────────────────────────
      const filtered = items.filter((item) => {
            return (
                  item.status === activeTab &&
                  (filterGenre === 'All' || item.genre === filterGenre) &&
                  (filterType === 'All' || item.type === filterType) &&
                  item.title?.toLowerCase().includes(searchQ.toLowerCase())
            );
      }).sort((a, b) => {
            const ta = a.createdAt?.toDate?.() ?? new Date(a.createdAt ?? 0);
            const tb = b.createdAt?.toDate?.() ?? new Date(b.createdAt ?? 0);
            return tb - ta;
      });

      // ─── Pagination ───────────────────────────────────────────
      const PAGE_SIZE = 30;
      const [currentPage, setCurrentPage] = useState(1);

      // reset to page 1 when filters/tab change
      useEffect(() => { setCurrentPage(1); }, [activeTab, filterGenre, filterType, searchQ]);

      const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
      const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

      const watchedCount = items.filter((i) => i.status === 'watched').length;
      const watchlistCount = items.filter((i) => i.status === 'watchlist').length;


      return (
            <>
                  <PageShapes />

                  <main className="movies-page">

                        {/* ── Hero ── */}
                        <section className="movies-hero">
                              <div className="container">
                                    <span className="movies-badge">🎬 My Movie Tracker</span>
                                    <h1 className="heading-font">Track What You <span>Watch</span></h1>
                                    <p>Keep a log of movies & series you've watched, and build your watchlist.</p>

                                    {/* Hero Search */}
                                    <div className="hero-search-wrap" ref={heroRef}>
                                          <div className="hero-search-box">
                                                <span className="hero-search-icon">🔍</span>
                                                <input
                                                      type="search"
                                                      className="hero-search-input"
                                                      placeholder="Search movies or series..."
                                                      value={heroQuery}
                                                      onChange={(e) => setHeroQuery(e.target.value)}
                                                />
                                                {heroSearching && <span className="hero-search-spinner" />}
                                          </div>

                                          {heroResults.length > 0 && (
                                                <ul className="hero-dropdown">
                                                      {heroResults.map((r) => {
                                                            const title = r.title || r.name;
                                                            const year = (r.release_date || r.first_air_date || '').slice(0, 4);
                                                            const type = r.media_type === 'tv' ? 'Series' : 'Movie';
                                                            const existing = items.find((i) => i.tmdbId === r.id);
                                                            return (
                                                                  <li key={r.id} className="hero-dropdown-item">
                                                                        <div className="hdi-left">
                                                                              {r.poster_path
                                                                                    ? <img src={`${IMG_BASE}${r.poster_path}`} alt={title} className="hdi-poster" />
                                                                                    : <div className="hdi-poster hdi-poster-placeholder">🎬</div>
                                                                              }
                                                                              <div className="hdi-info">
                                                                                    <span className="hdi-title">{title}</span>
                                                                                    <span className="hdi-meta">{type}{year ? ` · ${year}` : ''}</span>
                                                                              </div>
                                                                        </div>
                                                                        <div className="hdi-actions">
                                                                              {existing ? (
                                                                                    <button
                                                                                          className="hdi-btn already"
                                                                                          onClick={() => { setAlreadyModal(existing); setHeroResults([]); setHeroQuery(''); }}
                                                                                    >
                                                                                          {existing.status === 'watched' ? '✅ Watched' : '🕐 Watchlist'}
                                                                                    </button>
                                                                              ) : (
                                                                                    <>
                                                                                          <button
                                                                                                className="hdi-btn watched"
                                                                                                disabled={savingId === `${r.id}-watched`}
                                                                                                onClick={() => openQuickModal(r, 'watched')}
                                                                                          >
                                                                                                {savingId === `${r.id}-watched` ? '...' : '✅ Watched'}
                                                                                          </button>
                                                                                          <button
                                                                                                className="hdi-btn watchlist"
                                                                                                disabled={savingId === `${r.id}-watchlist`}
                                                                                                onClick={() => openQuickModal(r, 'watchlist')}
                                                                                          >
                                                                                                {savingId === `${r.id}-watchlist` ? '...' : '🕐 Watchlist'}
                                                                                          </button>
                                                                                    </>
                                                                              )}
                                                                        </div>
                                                                  </li>
                                                            );
                                                      })}
                                                </ul>
                                          )}
                                    </div>

                                    <div className="movies-stats">
                                          <div className="stat-chip"><strong>{watchedCount}</strong><span>Watched</span></div>
                                          <div className="stat-chip"><strong>{watchlistCount}</strong><span>Watchlist</span></div>
                                    </div>
                              </div>
                        </section>

                        {/* ── Body ── */}
                        <section className="movies-body">
                              <div className="container">

                                    {/* Tabs */}
                                    <div className="movies-tabs">
                                          {TABS.map((tab) => (
                                                <button
                                                      key={tab}
                                                      className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                                                      onClick={() => setActiveTab(tab)}
                                                >
                                                      {tab === 'watched' ? '✅ Watched' : '🕐 Watchlist'}
                                                      <span className="tab-count">{tab === 'watched' ? watchedCount : watchlistCount}</span>
                                                </button>
                                          ))}
                                    </div>

                                    {/* Controls */}
                                    <div className="movies-controls">
                                          <input
                                                className="movies-search"
                                                type="search"
                                                placeholder="Search title..."
                                                value={searchQ}
                                                onChange={(e) => setSearchQ(e.target.value)}
                                          />
                                          <select className="movies-filter" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                                <option value="All">All Types</option>
                                                <option value="Movie">Movie</option>
                                                <option value="Series">Series</option>
                                          </select>
                                          <select className="movies-filter" value={filterGenre} onChange={(e) => setFilterGenre(e.target.value)}>
                                                <option value="All">All Genres</option>
                                                {GENRES.map((g) => <option key={g}>{g}</option>)}
                                          </select>
                                          <button className="movies-add-btn" onClick={() => openModal(activeTab)}>
                                                + Add {activeTab === 'watched' ? 'Watched' : 'to Watchlist'}
                                          </button>
                                    </div>

                                    {/* Grid */}
                                    {loading ? (
                                          <div className="movies-loading"><Loading text="Loading your list..." /></div>
                                    ) : filtered.length === 0 ? (
                                          <div className="movies-empty">
                                                <p>{activeTab === 'watched' ? 'No watched movies yet. Add one!' : 'Your watchlist is empty. Add something to watch!'}</p>
                                                <button className="movies-add-btn" onClick={() => openModal(activeTab)}>+ Add Now</button>
                                          </div>
                                    ) : (
                                          <>
                                                <div className="movies-grid">
                                                      {paginated.map((item) => <MovieCard key={item.id} item={item} onMarkWatched={handleMarkWatched} />)}
                                                </div>

                                                {totalPages > 1 && (
                                                      <div className="movies-pagination">
                                                            <button
                                                                  className="pg-btn"
                                                                  disabled={currentPage === 1}
                                                                  onClick={() => setCurrentPage((p) => p - 1)}
                                                            >← Prev</button>

                                                            <div className="pg-numbers">
                                                                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                                                        .reduce((acc, p, idx, arr) => {
                                                                              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                                                              acc.push(p);
                                                                              return acc;
                                                                        }, [])
                                                                        .map((p, i) =>
                                                                              p === '...'
                                                                                    ? <span key={`dots-${i}`} className="pg-dots">…</span>
                                                                                    : <button
                                                                                          key={p}
                                                                                          className={`pg-num ${currentPage === p ? 'active' : ''}`}
                                                                                          onClick={() => setCurrentPage(p)}
                                                                                    >{p}</button>
                                                                        )
                                                                  }
                                                            </div>

                                                            <button
                                                                  className="pg-btn"
                                                                  disabled={currentPage === totalPages}
                                                                  onClick={() => setCurrentPage((p) => p + 1)}
                                                            >Next →</button>
                                                      </div>
                                                )}
                                          </>
                                    )}

                              </div>
                        </section>
                  </main>

                  {/* ── Modal ── */}
                  <Modal
                        open={showModal}
                        onClose={() => setShowModal(false)}
                        center
                        classNames={{ modal: 'movies-modal' }}
                  >
                        <div className="modal-header">
                              <h3 className="heading-font">
                                    {form.status === 'watched' ? '✅ Add Watched' : '🕐 Add to Watchlist'}
                              </h3>
                        </div>

                        <div className="modal-body">

                              {/* Status toggle */}
                              <div className="modal-status-toggle">
                                    <button className={form.status === 'watched' ? 'active' : ''} onClick={() => setForm((f) => ({ ...f, status: 'watched' }))}>
                                          ✅ Watched
                                    </button>
                                    <button className={form.status === 'watchlist' ? 'active' : ''} onClick={() => setForm((f) => ({ ...f, status: 'watchlist' }))}>
                                          🕐 Watchlist
                                    </button>
                              </div>

                              {/* Title */}
                              <div className="modal-field">
                                    <label>Title *</label>
                                    <input
                                          type="text"
                                          placeholder="e.g. Inception"
                                          value={form.title}
                                          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                    />
                              </div>

                              {/* Poster URL */}
                              <div className="modal-field">
                                    <label>Poster URL</label>
                                    <input
                                          type="url"
                                          placeholder="https://image.tmdb.org/..."
                                          value={form.poster || ''}
                                          onChange={(e) => setForm((f) => ({ ...f, poster: e.target.value }))}
                                    />
                                    {form.poster && (
                                          <img
                                                src={form.poster}
                                                alt="poster preview"
                                                className="modal-poster-preview"
                                                onError={(e) => e.target.style.display = 'none'}
                                          />
                                    )}
                              </div>

                              {/* Type + Year */}
                              <div className="modal-row">
                                    <div className="modal-field">
                                          <label>Type</label>
                                          <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                                                <option value="Movie">Movie</option>
                                                <option value="Series">Series</option>
                                          </select>
                                    </div>
                                    <div className="modal-field">
                                          <label>Year</label>
                                          <input
                                                type="number"
                                                placeholder="2024"
                                                min="1900"
                                                max="2030"
                                                value={form.year}
                                                onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                                          />
                                    </div>
                              </div>

                              {/* Genre + Language */}
                              <div className="modal-row">
                                    <div className="modal-field">
                                          <label>Genre</label>
                                          <select value={form.genre} onChange={(e) => setForm((f) => ({ ...f, genre: e.target.value }))}>
                                                <option value="">Select genre</option>
                                                {GENRES.map((g) => <option key={g}>{g}</option>)}
                                          </select>
                                    </div>
                                    <div className="modal-field">
                                          <label>Language</label>
                                          <select value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
                                                {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                                          </select>
                                    </div>
                              </div>

                              {/* Platform */}
                              <div className="modal-field">
                                    <label>Platform</label>
                                    <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}>
                                          <option value="">Select platform</option>
                                          {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
                                    </select>
                              </div>

                              {/* Rating — watched only */}
                              {form.status === 'watched' && (
                                    <div className="modal-field">
                                          <label>Your Rating</label>
                                          <div className="modal-rating">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                      <button
                                                            key={star}
                                                            className={`star-btn ${Number(form.rating) >= star ? 'active' : ''}`}
                                                            onClick={() => setForm((f) => ({ ...f, rating: String(star) }))}
                                                      >★</button>
                                                ))}
                                                {form.rating && <span className="rating-label">{form.rating}/5</span>}
                                          </div>
                                    </div>
                              )}

                              {/* Notes */}
                              <div className="modal-field">
                                    <label>Notes</label>
                                    <textarea
                                          placeholder="Any thoughts, quotes, or reminders..."
                                          rows={3}
                                          value={form.notes}
                                          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                    />
                              </div>

                        </div>

                        <div className="modal-footer">
                              <button className="modal-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                              <button className="modal-save" onClick={handleSave} disabled={saving || !form.title.trim()}>
                                    {saving ? 'Saving...' : 'Save'}
                              </button>
                        </div>
                  </Modal>

                  {/* ── Quick Save Modal ── */}
                  {quickModal && (
                        <Modal
                              open={!!quickModal}
                              onClose={() => setQuickModal(null)}
                              center
                              classNames={{ modal: 'movies-modal' }}
                        >
                              <div className="modal-header">
                                    <h3 className="heading-font">
                                          {quickModal.status === 'watched' ? '✅ Add to Watched' : '🕐 Add to Watchlist'}
                                    </h3>
                                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                                          {quickModal.result.title || quickModal.result.name}
                                    </p>
                              </div>
                              <div className="modal-body">

                                    {/* Platform */}
                                    <div className="modal-field">
                                          <label>Platform</label>
                                          <select value={quickPlatform} onChange={(e) => setQuickPlatform(e.target.value)}>
                                                <option value="">Select platform</option>
                                                {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
                                          </select>
                                    </div>

                                    {/* Rating — watched only */}
                                    {quickModal.status === 'watched' && (
                                          <div className="modal-field">
                                                <label>Your Rating</label>
                                                <div className="modal-rating">
                                                      {[1, 2, 3, 4, 5].map((star) => (
                                                            <button
                                                                  key={star}
                                                                  className={`star-btn ${Number(quickRating) >= star ? 'active' : ''}`}
                                                                  onClick={() => setQuickRating(String(star))}
                                                            >★</button>
                                                      ))}
                                                      {quickRating && <span className="rating-label">{quickRating}/5</span>}
                                                </div>
                                          </div>
                                    )}

                              </div>
                              <div className="modal-footer">
                                    <button className="modal-cancel" onClick={() => setQuickModal(null)}>Cancel</button>
                                    <button className="modal-save" onClick={handleHeroSave} disabled={!!savingId}>
                                          {savingId ? 'Saving...' : 'Save'}
                                    </button>
                              </div>
                        </Modal>
                  )}

                  {/* ── Already In List Modal ── */}
                  {alreadyModal && (
                        <Modal
                              open={!!alreadyModal}
                              onClose={() => setAlreadyModal(null)}
                              center
                              classNames={{ modal: 'movies-modal already-modal' }}
                        >
                              <div className="already-modal-inner">
                                    {alreadyModal.poster && (
                                          <img src={alreadyModal.poster} alt={alreadyModal.title} className="already-poster" />
                                    )}
                                    <div className="already-content">
                                          <span className="already-badge">
                                                {alreadyModal.status === 'watched' ? '✅ Already Watched' : '🕐 Already in Watchlist'}
                                          </span>
                                          <h3 className="heading-font">{alreadyModal.title}</h3>
                                          <div className="card-meta" style={{ marginTop: 6 }}>
                                                {alreadyModal.year && <span className="meta-chip">{alreadyModal.year}</span>}
                                                {alreadyModal.genre && <span className="meta-chip">{alreadyModal.genre}</span>}
                                                {alreadyModal.language && <span className="meta-chip">{alreadyModal.language}</span>}
                                                {alreadyModal.platform && <span className="meta-chip platform">{alreadyModal.platform}</span>}
                                                {alreadyModal.rating && <span className="meta-chip">{'★'.repeat(Number(alreadyModal.rating))} {alreadyModal.rating}/5</span>}
                                          </div>
                                    </div>
                              </div>
                              <div className="modal-footer">
                                    <button className="modal-cancel" onClick={() => setAlreadyModal(null)}>Close</button>
                              </div>
                        </Modal>
                  )}
            </>
      );
};

// ─── Movie Card ───────────────────────────────────────────────
const MovieCard = ({ item, onMarkWatched }) => {
      const typeColor = item.type === 'Series' ? 'series' : 'movie';
      const stars = Number(item.rating) || 0;
      return (
            <div className={`movie-card ${typeColor}`}>
                  {/* Poster */}
                  <div className="card-poster-wrap">
                        {item.poster
                              ? <img src={item.poster} alt={item.title} className="card-poster-img" />
                              : <div className="card-poster-fallback">🎬</div>
                        }
                        <div className="card-poster-overlay" />

                        {/* Top badges */}
                        <div className="card-badges">
                              <span className={`type-badge ${typeColor}`}>{item.type}</span>
                              {item.status === 'watchlist' && <span className="wl-badge">🕐</span>}
                        </div>

                        {/* Rating stars on poster */}
                        {item.status === 'watched' && stars > 0 && (
                              <div className="card-stars">
                                    {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
                              </div>
                        )}
                  </div>

                  {/* Info */}
                  <div className="card-info">
                        <h4 className="heading-font card-title">{item.title}</h4>
                        <div className="card-meta">
                              {item.year && <span className="meta-chip">{item.year}</span>}
                              {item.genre && <span className="meta-chip">{item.genre}</span>}
                              {item.language && <span className="meta-chip">{item.language}</span>}
                              {item.platform && <span className="meta-chip platform">{item.platform}</span>}
                        </div>
                        {item.notes && <p className="card-notes">{item.notes}</p>}
                        {item.status === 'watchlist' && (
                              <button className="mark-watched-btn" onClick={() => onMarkWatched(item.id)}>
                                    ✅ Mark as Watched
                              </button>
                        )}
                  </div>
            </div>
      );
};

export default Movies;
